import '../index.less';

import { Table, message } from 'antd';
import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../../store/store';
import { displayDate } from '../../../utils/datetime.util';

import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { getListingNoticesHistoryThunk } from '../../../services/noticesHistoryService/noticesHistoryThunk';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';

type NoticeHistoryProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const NoticeHistory: React.FC<NoticeHistoryProps> = (props) => {
  const { accountEnquiry } = props;
  const dispatch = useDispatch<AppDispatch>();

  const [noticesHistoryData, setNoticesHistoryData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: noticesHistoryData?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    currentPage: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No.',
      key: 'index',
      render: (_, __, index: number) =>
        (paginationState.current - 1) * paginationState.pageSize + index + 1,
    },
    {
      title: 'Issued Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'Notices / Letter',
      dataIndex: 'notices',
      key: 'notices',
      render: (text, record) => {
        return record?.noticesGenerationBatch?.batchNo;
      },
    },
  ];

  const fetchNoticeHistory = async (params?: any) => {
    setLoading(true);
    await dispatch(getListingNoticesHistoryThunk({ ...params }))
      .unwrap()
      .then((res) => {
        setNoticesHistoryData(res);
      })
      .catch((err) => {
        message.error('Failed to fetch notice history');
        console.error('Failed to fetch notice history', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchNoticeHistory({
      ...initialParams,
      ...filterState,
      pagination,
      accountDetailsID: accountEnquiry?.accountDetails?.id,
    });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useEffect(() => {
    if (accountEnquiry?.accountDetails?.id) {
      fetchNoticeHistory({
        ...initialParams,
        accountDetailsID: accountEnquiry?.accountDetails?.id,
      });
    }
  }, [accountEnquiry?.accountDetails?.id]);

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="info-container">
          <Table
            columns={columns}
            dataSource={noticesHistoryData?.data}
            loading={loading}
            pagination={tablePagination}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default NoticeHistory;
